import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { usePrefersReducedMotion } from '../hooks/usePrefersReducedMotion'
import { usePresence } from '../hooks/usePresence'
import { menuSize, placeAnchoredMenu } from '../utils/menuPlacement'
import { formatEuroFr } from '../utils/editProductOutcome'
import {
  articleCountLabel,
  filterCartsForShare,
  shouldShowShareCartSearch,
} from '../utils/sharedCartPresentation'
import '../styles/scrollbar.css'
import './ConversationCartPicker.css'

export default function ConversationCartPicker({
  isOpen,
  anchor,
  carts = [],
  onClose,
  onSelect,
}) {
  const menuRef = useRef(null)
  const frostRef = useRef(null)
  const reducedMotion = usePrefersReducedMotion()
  const { mounted, leaving } = usePresence(isOpen, { duration: 180, reduced: reducedMotion })
  const [query, setQuery] = useState('')
  const [coords, setCoords] = useState({ left: 0, top: 0, origin: 'bottom right' })
  const options = useMemo(() => filterCartsForShare(carts, query), [carts, query])
  const showSearch = shouldShowShareCartSearch(filterCartsForShare(carts).length)

  useEffect(() => {
    if (isOpen) setQuery('')
  }, [isOpen])

  useLayoutEffect(() => {
    if (!mounted || !anchor) return undefined
    const update = () => {
      const size = menuSize(menuRef.current)
      const rect = anchor.getBoundingClientRect?.() || anchor
      const placed = placeAnchoredMenu({
        trigger: rect,
        width: Math.max(size.width, 300),
        height: size.height,
        vw: window.innerWidth,
        vh: window.innerHeight,
        align: 'end',
        prefer: 'above',
      })
      const originY = placed.top + size.height / 2 < (rect.top || 0) ? 'bottom' : 'top'
      const next = { ...placed, origin: `${originY} right` }
      setCoords((prev) => (
        prev.left === next.left && prev.top === next.top && prev.origin === next.origin
          ? prev
          : next
      ))
      if (frostRef.current) void getComputedStyle(frostRef.current).backdropFilter
    }
    update()
    window.addEventListener('resize', update)
    window.addEventListener('scroll', update, true)
    return () => {
      window.removeEventListener('resize', update)
      window.removeEventListener('scroll', update, true)
    }
  }, [mounted, anchor, options.length, showSearch])

  useEffect(() => {
    if (!mounted) return undefined
    const onPointer = (event) => {
      if (menuRef.current?.contains(event.target) || anchor?.contains?.(event.target)) return
      onClose?.()
    }
    const onKey = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        onClose?.()
      }
    }
    document.addEventListener('mousedown', onPointer)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onPointer)
      document.removeEventListener('keydown', onKey)
    }
  }, [mounted, onClose, anchor])

  if (!mounted || typeof document === 'undefined') return null

  return createPortal(
    <div className="share-cart-picker-root" style={{ left: coords.left, top: coords.top }}>
      <div className="lg-blur-prewarm" aria-hidden="true" />
        <div
        ref={menuRef}
        className={`lg-panel share-cart-picker${reducedMotion ? '' : leaving ? ' is-leaving' : ' is-animated'}`}
        role="listbox"
        aria-label="Paniers à partager"
        style={{ transformOrigin: coords.origin }}
      >
        <div className="lg-panel-frost" ref={frostRef} />
        <div className="lg-panel-tint" />
        <div className="lg-panel-shine" />
        <div className={`lg-panel-body share-cart-picker-motion${reducedMotion ? '' : leaving ? ' is-leaving' : ' is-animated'}`}>
          {showSearch ? (
            <input
              className="share-cart-picker-search"
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Rechercher un panier"
              aria-label="Rechercher un panier"
            />
          ) : null}
          <div className="share-cart-picker-list lg-scroll">
            {options.length === 0 ? (
              <p className="share-cart-picker-empty">Aucun panier disponible.</p>
            ) : options.map((cart) => (
              <button
                key={cart.id}
                type="button"
                className="share-cart-picker-item"
                role="option"
                onClick={() => {
                  onSelect?.(cart)
                  onClose?.()
                }}
              >
                <span className="share-cart-picker-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" width="16" height="16">
                    <path d="M6 7h13l-1.2 8.2a2 2 0 0 1-2 1.7H9.4a2 2 0 0 1-2-1.6L6 4H3" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                    <circle cx="10" cy="20" r="1.2" fill="currentColor" />
                    <circle cx="17" cy="20" r="1.2" fill="currentColor" />
                  </svg>
                </span>
                <span className="share-cart-picker-name" title={cart.name}>{cart.name}</span>
                <span className="share-cart-picker-count">{articleCountLabel(cart.items_count)}</span>
                <span className="share-cart-picker-total">{formatEuroFr(cart.total)}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  )
}
